import useFilePermissions from "~/hooks/useFilePermissions";

/**
 * Determines whether a user has read or write permissions for a specific file or directory.
 *
 * Handles various permission scenarios:
 * - Wildcard permissions (paths ending with "/*")
 * - Exact path matches
 * - Parent directory permissions (read access to parent paths)
 *
 * @param {string} key - The file or directory path to check permissions for
 * @param {string} permission - The permission type to check ("read" or "write")
 * @returns {boolean|null} True if permitted, false if denied, null if loading
 *
 * @example
 * const canWrite = useFileAccess("raw/my-folder", "write");
 */
const useFileAccess = (key = "", permission: "read" | "write") => {
  const { data: permissions = {}, isLoading } = useFilePermissions();

  if (isLoading) {
    return null;
  }

  // Grant paths never carry a trailing slash, but directory keys sometimes do
  // (e.g. "foo/bar/"). Normalize so the segment-boundary checks below line up.
  const normalizedKey = key.endsWith("/") ? key.slice(0, -1) : key;

  return Object.entries(permissions).some(([path, value]) => {
    if (!value.includes(permission)) {
      return false;
    }

    // Exact match
    if (path === normalizedKey) {
      return true;
    }

    // Wildcard grant (e.g. "foo/*") covers the folder itself and anything nested
    // beneath it. Match on path-segment boundaries so a grant on "foo/*" does not
    // leak to a sibling like "foo-bar".
    if (path.endsWith("/*")) {
      const prefix = path.slice(0, -2);
      if (normalizedKey === prefix || normalizedKey.startsWith(`${prefix}/`)) {
        return true;
      }
    }

    // The user has access to something nested below `key`, so they need read
    // access to `key` in order to navigate down to it. Only grant read, and
    // require a segment boundary so "foo" is not treated as an ancestor of
    // "foo-bar/...".
    if (permission === "read" && path.startsWith(`${normalizedKey}/`)) {
      return true;
    }

    return false;
  });
};

export default useFileAccess;
